import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import UserAvatar from "@/components/UserAvatar";
import { toast } from "sonner";
import {
  Heart, ChatCircle, Plus, Trash, PencilSimple, UploadSimple, Camera, X,
  UsersThree, SquaresFour, PaperPlaneRight, Buildings, PlayCircle, BookOpenText,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString("es-MX");
}

function roleColor(r) {
  if (r === "admin") return "text-[#CCFF00]";
  if (r === "directivo") return "text-blue-400";
  if (r === "profesor") return "text-yellow-400";
  return "text-gray-400";
}

export default function Community() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clubs, setClubs] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCaption, setUploadCaption] = useState("");
  const [uploadClubId, setUploadClubId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentsByPost, setCommentsByPost] = useState({});
  const [newComment, setNewComment] = useState({});
  const [showSections, setShowSections] = useState(false);

  const loadFeed = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/feed?limit=50");
      setPosts(data || []);
    } catch (e) { toast.error("Error al cargar el muro"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user) return;
    loadFeed();
    api.get("/clubs").then(({ data }) => {
      setClubs(data || []);
      // Auto-pick user's club if any
      if (user.club_id) setUploadClubId(user.club_id);
      else if (data?.[0]?.id) setUploadClubId(data[0].id);
    }).catch(() => {});
  }, [user]);

  const submitUpload = async () => {
    if (!uploadFile) { toast.error("Selecciona una foto o video"); return; }
    if (!uploadClubId) { toast.error("Elige un club"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      if (uploadCaption) fd.append("caption", uploadCaption);
      await api.post(`/clubs/${uploadClubId}/posts`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Publicación creada");
      setUploadOpen(false);
      setUploadFile(null);
      setUploadCaption("");
      loadFeed();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al publicar"); }
    finally { setUploading(false); }
  };

  const submitEdit = async () => {
    if (!editing) return;
    try {
      await api.patch(`/posts/${editing.id}`, { caption: editing.caption });
      toast.success("Publicación editada");
      setEditing(null);
      loadFeed();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("¿Eliminar esta publicación?")) return;
    try {
      await api.delete(`/posts/${postId}`);
      toast.success("Publicación eliminada");
      setPosts((p) => p.filter((x) => x.id !== postId));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const toggleLike = async (post) => {
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setPosts((prev) => prev.map((p) => p.id === post.id
        ? { ...p, likes: data.liked ? [...(p.likes || []), user.id] : (p.likes || []).filter((u) => u !== user.id) }
        : p
      ));
    } catch { /* silent */ }
  };

  const toggleComments = async (postId) => {
    const isOpen = !!expandedComments[postId];
    setExpandedComments((s) => ({ ...s, [postId]: !isOpen }));
    if (!isOpen && !commentsByPost[postId]) {
      try {
        const { data } = await api.get(`/posts/${postId}/comments`);
        setCommentsByPost((s) => ({ ...s, [postId]: data }));
      } catch { /* silent */ }
    }
  };

  const submitComment = async (postId) => {
    const text = (newComment[postId] || "").trim();
    if (!text) return;
    try {
      await api.post(`/posts/${postId}/comments`, { text });
      setNewComment((s) => ({ ...s, [postId]: "" }));
      const { data } = await api.get(`/posts/${postId}/comments`);
      setCommentsByPost((s) => ({ ...s, [postId]: data }));
    } catch { /* silent */ }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-6" data-testid="community-page">
      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-white/10" data-testid="community-tabs">
        <button
          className="px-3 py-2 text-[11px] uppercase tracking-widest font-bold border-b-2 border-[#CCFF00] text-[#CCFF00]"
          data-testid="tab-muro"
        >
          <UsersThree size={14} className="inline mr-1" /> Muro
        </button>
        <Link
          to="/dashboard/comic"
          className="px-3 py-2 text-[11px] uppercase tracking-widest font-bold border-b-2 border-transparent text-gray-400 hover:text-[#CCFF00] transition-colors"
          data-testid="tab-comic"
        >
          <BookOpenText size={14} className="inline mr-1" /> Cómic
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] font-bold flex items-center gap-1.5">
            <UsersThree size={12} weight="fill" /> Comunidad · Muro
          </p>
          <h1 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-black uppercase tracking-tighter leading-none mt-1">
            Comparte tu juego
          </h1>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowSections((s) => !s)}
            className="border-white/15 text-gray-300 hover:text-[#CCFF00] text-[10px] uppercase h-9"
            data-testid="toggle-sections"
          >
            <SquaresFour size={14} className="mr-1" /> {showSections ? "Ocultar" : "Ver"} apartados
          </Button>
          <Button
            onClick={() => setUploadOpen(true)}
            className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide"
            data-testid="community-new-post"
          >
            <Plus size={14} className="mr-1" /> Publicar
          </Button>
        </div>
      </div>

      {/* Feed */}
      {loading ? (
        <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
      ) : posts.length === 0 ? (
        <div className="card-tactical p-8 text-center" data-testid="community-empty">
          <UsersThree size={40} className="mx-auto text-gray-600 mb-2" />
          <p className="text-sm text-gray-400">Aún no hay publicaciones. ¡Sé el primero en compartir una foto o video!</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="community-feed">
          {posts.map((p) => {
            const isVideo = (p.media_content_type || "").startsWith("video/");
            const canEdit = p.author_id === user?.id || user?.role === "admin" || (user?.role === "directivo" && (user?.club_id === p.club_id || (user?.club_ids || []).includes(p.club_id)));
            const iLike = (p.likes || []).includes(user?.id);
            const commentsOpen = !!expandedComments[p.id];
            const mediaUrl = `${BACKEND_URL}/api/posts/${p.id}/media`;
            return (
              <article key={p.id} className="card-tactical overflow-hidden" data-testid={`post-${p.id}`}>
                {/* Header */}
                <header className="p-3 flex items-center gap-3">
                  <UserAvatar
                    src={p.author_photo}
                    fallback={(p.author_name || "?").slice(0, 2)}
                    className="w-10 h-10 rounded-full border border-white/10"
                  />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-bold ${roleColor(p.author_role)}`}>{p.author_name || "Usuario"}</p>
                    <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
                      <Buildings size={10} />
                      <span className="truncate">{p.club_name || "Club"}</span>
                      <span>·</span>
                      <span>{timeAgo(p.created_at)}</span>
                      {p.edited_at && <span className="italic">· editado</span>}
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setEditing({ ...p })}
                        className="text-gray-400 hover:text-[#CCFF00] h-7 w-7 p-0"
                        data-testid={`post-edit-${p.id}`}
                        title="Editar"
                      >
                        <PencilSimple size={14} />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => deletePost(p.id)}
                        className="text-red-400 hover:bg-red-500/10 h-7 w-7 p-0"
                        data-testid={`post-delete-${p.id}`}
                        title="Eliminar"
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  )}
                </header>

                {/* Media */}
                <div className="relative bg-black">
                  {isVideo ? (
                    <video src={mediaUrl} controls playsInline className="w-full max-h-[70vh] object-contain bg-black" data-testid={`post-video-${p.id}`}>
                      <track kind="captions" />
                    </video>
                  ) : (
                    <img src={mediaUrl} alt={p.caption || "post"} className="w-full max-h-[70vh] object-contain bg-black" loading="lazy" />
                  )}
                  {isVideo && (
                    <div className="absolute top-2 left-2 bg-black/60 rounded px-2 py-0.5 text-[10px] text-white flex items-center gap-1">
                      <PlayCircle size={12} weight="fill" /> Video
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="p-3 border-t border-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      onClick={() => toggleLike(p)}
                      className={`flex items-center gap-1 text-xs uppercase tracking-widest font-bold ${iLike ? "text-red-400" : "text-gray-400 hover:text-white"}`}
                      data-testid={`post-like-${p.id}`}
                    >
                      <Heart size={16} weight={iLike ? "fill" : "regular"} /> {(p.likes || []).length}
                    </button>
                    <button
                      onClick={() => toggleComments(p.id)}
                      className="flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-gray-400 hover:text-white"
                      data-testid={`post-comments-${p.id}`}
                    >
                      <ChatCircle size={16} /> {p.comments_count || 0}
                    </button>
                  </div>
                  {p.caption && <p className="text-sm text-gray-200 whitespace-pre-wrap"><b className={roleColor(p.author_role)}>{p.author_name}</b> · {p.caption}</p>}

                  {commentsOpen && (
                    <div className="mt-3 space-y-2" data-testid={`post-comments-panel-${p.id}`}>
                      {(commentsByPost[p.id] || []).map((c) => (
                        <div key={c.id} className="flex items-start gap-2 text-xs">
                          <UserAvatar src={c.author_photo} fallback={(c.author_name || "?").slice(0, 2)} className="w-6 h-6 rounded-full shrink-0" />
                          <div className="flex-1 bg-black/40 p-2 border border-white/10 rounded">
                            <p className="text-white"><b className={roleColor(c.author_role)}>{c.author_name}</b> {c.text}</p>
                            <p className="text-[10px] text-gray-500 mt-0.5">{timeAgo(c.created_at)}</p>
                          </div>
                        </div>
                      ))}
                      <div className="flex gap-2 mt-2">
                        <Input
                          value={newComment[p.id] || ""}
                          onChange={(e) => setNewComment((s) => ({ ...s, [p.id]: e.target.value }))}
                          onKeyDown={(e) => e.key === "Enter" && submitComment(p.id)}
                          placeholder="Escribe un comentario…"
                          className="bg-black/60 border-white/15 h-9 text-xs"
                          data-testid={`comment-input-${p.id}`}
                        />
                        <Button
                          onClick={() => submitComment(p.id)}
                          size="sm"
                          className="bg-[#CCFF00] text-black hover:bg-[#B3E600] h-9 px-3"
                          data-testid={`comment-send-${p.id}`}
                        >
                          <PaperPlaneRight size={14} />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Sections drawer (toggleable) */}
      {showSections && <SectionsGrid />}

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={(v) => !v && setUploadOpen(false)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md" data-testid="community-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-black uppercase">Nueva publicación</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {clubs.length > 1 && (
              <div>
                <label className="text-[10px] uppercase tracking-widest text-gray-400 mb-1 block">Club</label>
                <Select value={uploadClubId} onValueChange={setUploadClubId}>
                  <SelectTrigger className="bg-black/60 border-white/15 text-white text-xs h-9" data-testid="upload-club">
                    <SelectValue placeholder="Elige un club" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10 text-white max-h-72">
                    {clubs.map((c) => <SelectItem key={c.id} value={c.id} className="text-xs">{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => fileRef.current?.click()}
                className="flex-1 border-[#CCFF00]/40 text-[#CCFF00] h-11 text-xs uppercase"
                data-testid="upload-file-btn"
              >
                <UploadSimple size={16} className="mr-1" /> Foto o video
              </Button>
              <Button
                variant="outline"
                onClick={() => cameraRef.current?.click()}
                className="flex-1 border-[#FFD24A]/40 text-[#FFD24A] h-11 text-xs uppercase"
                data-testid="upload-camera-btn"
              >
                <Camera size={16} className="mr-1" /> Tomar foto
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/*"
                className="hidden"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                data-testid="upload-file-input"
              />
              <input
                ref={cameraRef}
                type="file"
                accept="image/*,video/*"
                capture="environment"
                className="hidden"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                data-testid="upload-camera-input"
              />
            </div>
            {uploadFile && (
              <div className="flex items-center gap-2 p-2 bg-black/40 border border-white/10 rounded" data-testid="upload-selected">
                <div className="text-[10px] text-gray-300 truncate flex-1">{uploadFile.name} · {(uploadFile.size / 1024 / 1024).toFixed(1)} MB</div>
                <Button variant="ghost" size="sm" onClick={() => setUploadFile(null)} className="text-red-400 h-7 w-7 p-0"><X size={12} /></Button>
              </div>
            )}
            <Textarea
              value={uploadCaption}
              onChange={(e) => setUploadCaption(e.target.value)}
              placeholder="Escribe una descripción (opcional)…"
              rows={3}
              className="bg-black/60 border-white/15 text-sm"
              data-testid="upload-caption"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} className="text-xs uppercase" data-testid="upload-cancel">Cancelar</Button>
            <Button onClick={submitUpload} disabled={uploading || !uploadFile || !uploadClubId} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="upload-submit">
              {uploading ? "Publicando…" : "Publicar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md" data-testid="community-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-black uppercase">Editar publicación</DialogTitle>
          </DialogHeader>
          {editing && (
            <Textarea
              value={editing.caption || ""}
              onChange={(e) => setEditing((s) => ({ ...s, caption: e.target.value }))}
              rows={4}
              className="bg-black/60 border-white/15 text-sm"
              data-testid="edit-caption"
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="text-xs uppercase">Cancelar</Button>
            <Button onClick={submitEdit} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="edit-submit">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------- Sections mini-grid (togglable) -------------------------
const SECTIONS_MINI = [
  { key: "torneos", label: "Torneos", path: "/dashboard/torneos" },
  { key: "retas", label: "Retas", path: "/dashboard/retas" },
  { key: "ligas", label: "Ligas", path: "/dashboard/ligas" },
  { key: "marcador", label: "Marcador", path: "/dashboard/marcador" },
  { key: "balonpie", label: "Balonpié", path: "/dashboard/balonpie" },
  { key: "clubes", label: "Clubes", path: "/dashboard/clubes" },
  { key: "clinicas", label: "Clínicas", path: "/dashboard/clinicas" },
  { key: "jugadores", label: "Jugadores", path: "/dashboard/jugadores" },
  { key: "estadisticas", label: "Estadísticas", path: "/dashboard/estadisticas" },
  { key: "tienda", label: "Tienda", path: "/dashboard/tienda" },
  { key: "chat", label: "Chat", path: "/dashboard/chat" },
  { key: "mi_tarjeta", label: "Mi Tarjeta", path: "/dashboard/mi-tarjeta" },
];

function SectionsGrid() {
  return (
    <div className="card-tactical p-3" data-testid="community-sections-grid">
      <p className="text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] font-bold mb-2">Explora la plataforma</p>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {SECTIONS_MINI.map((s) => (
          <Link
            key={s.key}
            to={s.path}
            className="p-2 border border-white/10 bg-black/40 hover:border-[#CCFF00]/40 hover:bg-[#CCFF00]/5 text-center text-[10px] uppercase tracking-widest font-bold text-gray-300 hover:text-[#CCFF00] transition-colors"
            data-testid={`section-mini-${s.key}`}
          >
            {s.label}
          </Link>
        ))}
      </div>
    </div>
  );
}
